/**
 * AdminTenantDetail — Super-Admin tenant inspector + feature override
 *
 * Accessed by clicking a row in /admin/tenants. Shows full tenant state
 * (plan, effective flags, add-ons, wallet, settings) and lets admin override
 * any feature flag on a per-tenant basis without leaving the page.
 */
import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { SearchableSelect } from "@/components/SearchableSelect";
import {
    ArrowLeft, Building2, CreditCard, ToggleLeft, Sparkles, Wallet, Settings,
    Check, X, RotateCcw, RefreshCw, Users, Mail, Phone, Key, Copy, Save, ShieldOff, ShieldCheck,
    LayoutDashboard, BookOpen, ClipboardList, Monitor, BarChart2, MessageSquare,
    Gavel, AlertTriangle, TrendingUp, LogOut, MapPin, Globe, Cpu, Calendar,
} from "lucide-react";
import { fmtDate } from "@/lib/time";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";

const TABS = [
    { id: "overview",   label: "Overview",        icon: Building2 },
    { id: "dashboard",  label: "Dashboard",       icon: LayoutDashboard },
    { id: "team",       label: "Team & access",   icon: Users },
    { id: "flags",      label: "Features",        icon: ToggleLeft },
    { id: "addons",     label: "Add-ons",         icon: Sparkles },
    { id: "passbook",   label: "Passbook",        icon: BookOpen },
    { id: "audit",      label: "Audit log",       icon: ClipboardList },
    { id: "sessions",   label: "Sessions",        icon: Monitor },
    { id: "usage",      label: "Usage",           icon: BarChart2 },
    { id: "comms",      label: "Communications",  icon: MessageSquare },
    { id: "settings",   label: "Integrations",    icon: Settings },
];

export default function AdminTenantDetail() {
    const { tenantId } = useParams();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [plansV2, setPlansV2] = useState([]);
    const [tab, setTab] = useState("overview");

    const load = useCallback(async () => {
        try {
            const [d, p] = await Promise.all([
                api.get(`/admin/tenants/${tenantId}/detail`).then(r => r.data),
                api.get(`/admin/plans-v2`).then(r => r.data).catch(() => []),
            ]);
            setData(d);
            setPlansV2((p || []).filter(x => x.kind));
        } catch (e) { toast.error(apiError(e)); }
    }, [tenantId]);

    useEffect(() => { const t = setTimeout(load, 0); return () => clearTimeout(t); }, [load]);

    if (!data) return <div className="text-sm text-muted-foreground p-6" data-testid="tenant-detail-loading">Loading tenant…</div>;
    const { tenant } = data;

    return (
        <div className="space-y-6" data-testid="admin-tenant-detail">
            <button onClick={() => nav("/admin/tenants")} data-testid="tenant-detail-back"
                className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5">
                <ArrowLeft className="w-3.5 h-3.5"/> Back to tenants
            </button>

            {/* Hero */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="overline">Tenant</div>
                    <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground mt-1" data-testid="tenant-detail-name">{tenant.name}</h1>
                    <div className="mt-1 flex items-center gap-3 text-sm text-muted-foreground">
                        <span className="font-mono">{tenant.slug}</span>
                        <span>·</span>
                        <span className="capitalize">{tenant.status || "—"}</span>
                        <span>·</span>
                        <span>Joined {fmtDate(tenant.created_at)}</span>
                    </div>
                    <div className="mt-1 text-[11px] text-muted-foreground font-mono">ID {tenant.id}</div>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={load} data-testid="tenant-detail-refresh"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-border hover:border-primary rounded-lg text-xs font-medium text-foreground">
                        <RefreshCw className="w-3.5 h-3.5"/> Refresh
                    </button>
                </div>
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <KpiCard label="Plan" value={data.plan?.name || tenant.plan_code || "—"} sub={data.plan?.price_monthly_inr != null ? `₹${data.plan.price_monthly_inr}/mo · ${data.plan.seats_default} seats` : null} testId="kpi-plan"/>
                <KpiCard label="Users" value={data.counts.users} testId="kpi-users"/>
                <KpiCard label="Litigations" value={data.counts.cases} testId="kpi-cases"/>
                <KpiCard label="Add-ons" value={data.addons.active_count} sub={`₹${data.addons.monthly_total_inr.toLocaleString("en-IN")}/mo`} testId="kpi-addons"/>
                <KpiCard label="Wallet" value={`₹${data.wallet.balance_inr.toLocaleString("en-IN")}`} sub={`${data.wallet.txn_count} txns`} testId="kpi-wallet"/>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 border-b border-border overflow-x-auto scrollbar-hide" data-testid="tenant-detail-tabs">
                {TABS.map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)}
                        data-testid={`tenant-detail-tab-${t.id}`}
                        className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors shrink-0 whitespace-nowrap ${
                            tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
                        }`}>
                        <t.icon className="w-4 h-4"/> {t.label}
                    </button>
                ))}
            </div>

            {tab === "overview"  && <OverviewTab data={data} plansV2={plansV2} reload={load}/>}
            {tab === "dashboard" && <DashboardViewTab tenantId={tenantId}/>}
            {tab === "team"      && <TeamTab data={data} tenantId={tenantId} reload={load}/>}
            {tab === "flags"     && <FlagsTab data={data} tenantId={tenantId} reload={load}/>}
            {tab === "addons"    && <AddonsTab data={data}/>}
            {tab === "passbook"  && <PassbookTab tenantId={tenantId} data={data}/>}
            {tab === "audit"     && <AuditLogTab tenantId={tenantId}/>}
            {tab === "sessions"  && <SessionsTab tenantId={tenantId}/>}
            {tab === "usage"     && <UsageTab tenantId={tenantId}/>}
            {tab === "comms"     && <CommsTab tenantId={tenantId}/>}
            {tab === "settings"  && <SettingsTab data={data}/>}
        </div>
    );
}

/* ── Overview ─────────────────────────────────────────────────────────── */
function OverviewTab({ data, plansV2, reload }) {
    const [planCode, setPlanCode] = useState(data.tenant.plan_code || "");
    const [saving, setSaving] = useState(false);

    const savePlan = async () => {
        const reason = window.prompt("Reason for plan change (mandatory · audit-logged):");
        if (!reason || reason.trim().length < 3) { toast.error("Reason required (3+ chars)"); return; }
        setSaving(true);
        try {
            await api.patch(`/admin/tenants/${data.tenant.id}/plan`, { plan_code: planCode, reason });
            toast.success("Plan updated · audit-logged");
            await reload();
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <div className="space-y-4" data-testid="tenant-overview-tab">

            {/* ── Firm Owner Details ─────────────────────────────────── */}
            <FirmOwnerCard owner={data.firm_owner} addr={data.tenant.billing_address_components}/>

            <div className="grid md:grid-cols-2 gap-4">
            <Card title="Plan assignment" testId="card-plan">
                <div className="space-y-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Current plan</div>
                        <div className="mt-1 text-foreground font-medium">{data.plan?.name || data.tenant.plan_code || "— no plan —"}</div>
                        <div className="text-xs text-muted-foreground font-mono">{data.plan?.code} · resolved via {data.plan?.lookup_via || "none"}</div>
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Reassign plan</label>
                        <div className="mt-1">
                            <SearchableSelect
                                value={planCode} onChange={setPlanCode}
                                placeholder="— select a plan —"
                                allowClear testId="plan-select"
                                options={plansV2.map(p => ({
                                    value: p.code,
                                    label: `${p.name} · ₹${p.price_monthly_inr || 0}/mo`,
                                    sub: `${p.code} · ${p.seats_default || "?"} seats`,
                                }))}/>
                        </div>
                        <button onClick={savePlan} disabled={saving || !planCode || planCode === data.tenant.plan_code}
                            data-testid="plan-save"
                            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:opacity-90 disabled:opacity-40">
                            {saving ? "Saving…" : "Reassign plan"}
                        </button>
                    </div>
                </div>
            </Card>

            <Card title="Branding" testId="card-branding">
                <div className="space-y-2 text-sm">
                    <Row k="App name" v={data.tenant.branding.app_name || "—"}/>
                    <Row k="Tagline" v={data.tenant.branding.brand_tagline || "—"}/>
                    <Row k="Primary color" v={data.tenant.branding.primary_color || "—"} mono/>
                    <Row k="Logo" v={data.tenant.branding.logo_path ? "✓ Uploaded" : "—"}/>
                </div>
            </Card>

            <Card title="Lifecycle" testId="card-lifecycle">
                <div className="space-y-2 text-sm">
                    <Row k="Status" v={data.tenant.status || "—"}/>
                    <Row k="Active flag" v={String(data.tenant.is_active)} mono/>
                    <Row k="Joined" v={fmtDate(data.tenant.created_at)}/>
                    <Row k="Trial ends" v={data.tenant.trial_ends_at ? fmtDate(data.tenant.trial_ends_at) : "—"}/>
                    <Row k="Deleted" v={data.tenant.deleted_at ? fmtDate(data.tenant.deleted_at) : "—"}/>
                </div>
            </Card>

            <Card title="Plan policies" testId="card-plan-policies">
                <div className="space-y-2 text-sm">
                    <Row k="Kind" v={data.plan?.kind || "—"}/>
                    <Row k="Seats included" v={data.plan?.seats_default ?? "—"}/>
                    <Row k="Auto CA Pack (Apr 5)" v={data.plan?.auto_ca_pack_on_apr_5 ? "✓ Opted in" : "off"}/>
                    <Row k="Monthly price" v={data.plan?.price_monthly_inr != null ? `₹${data.plan.price_monthly_inr}` : "—"} mono/>
                    <Row k="Annual price" v={data.plan?.price_annual_inr != null ? `₹${data.plan.price_annual_inr}` : "—"} mono/>
                </div>
            </Card>
            </div>
        </div>
    );
}

/* ── Features tab — full override grid ─────────────────────────────────── */
function FlagsTab({ data, tenantId, reload }) {
    const { feature_catalog, flags } = data;
    const [busyKey, setBusyKey] = useState(null);
    const [query, setQuery] = useState("");

    const toggleFlag = async (key, newValue) => {
        const reason = window.prompt(`Override "${key}" → ${newValue === null ? "INHERIT" : String(newValue)}?\nEnter reason (mandatory · audit-logged):`);
        if (!reason || reason.trim().length < 3) { toast.error("Reason required (3+ chars)"); return; }
        setBusyKey(key);
        try {
            await api.patch(`/admin/tenants/${tenantId}/flag-override`, {
                key, value: newValue, reason,
            });
            toast.success(`"${key}" → ${newValue === null ? "inherit" : String(newValue)}`);
            await reload();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusyKey(null); }
    };

    const grouped = {};
    for (const f of feature_catalog) {
        const cat = f.category || "Misc";
        if (query && !f.key.toLowerCase().includes(query.toLowerCase()) && !(f.label || "").toLowerCase().includes(query.toLowerCase())) continue;
        grouped[cat] = grouped[cat] || [];
        grouped[cat].push(f);
    }

    return (
        <div className="space-y-5" data-testid="tenant-flags-tab">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-sm text-muted-foreground max-w-xl">
                    Override individual feature flags for this tenant. Effective resolution:
                    <span className="font-mono mx-1 text-foreground">default ← plan ← tenant</span>
                    A tenant override wins. Click <em className="not-italic font-medium">Inherit</em> to remove the override and fall back to the plan default.
                </p>
                <input type="text" value={query} onChange={e => setQuery(e.target.value)}
                    placeholder="Filter…"
                    data-testid="flags-filter"
                    className="px-3 py-1.5 bg-background border border-border focus:border-primary outline-none rounded-lg text-sm w-56"/>
            </div>

            {Object.entries(grouped).map(([cat, items]) => (
                <div key={cat} className="rounded-xl border border-border bg-card overflow-x-auto" data-testid={`flag-group-${cat}`}>
                    <div className="px-4 py-2 border-b border-border bg-muted/30 text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">{cat}</div>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                                <th className="text-left px-4 py-2">Feature</th>
                                <th className="text-center px-2 py-2 w-24">Default</th>
                                <th className="text-center px-2 py-2 w-24">Plan</th>
                                <th className="text-center px-2 py-2 w-32">Effective</th>
                                <th className="text-right px-4 py-2 w-72">Override</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(f => {
                                const effective = flags.effective[f.key];
                                const source = flags.source[f.key];
                                const planFlag = flags.plan_flags[f.key];
                                const tenantOverride = flags.tenant_overrides[f.key];
                                const isOverridden = Object.prototype.hasOwnProperty.call(flags.tenant_overrides, f.key);
                                return (
                                    <tr key={f.key} className="border-t border-border/50" data-testid={`flag-row-${f.key}`}>
                                        <td className="px-4 py-2.5">
                                            <div className="text-foreground font-medium">{f.label || f.key}</div>
                                            <div className="text-[10px] text-muted-foreground font-mono">{f.key}</div>
                                        </td>
                                        <td className="text-center"><FlagPill v={flags.defaults[f.key]}/></td>
                                        <td className="text-center">{planFlag === undefined ? <span className="text-[10px] text-muted-foreground">—</span> : <FlagPill v={planFlag}/>}</td>
                                        <td className="text-center">
                                            <div className="flex items-center justify-center gap-1.5">
                                                <FlagPill v={effective} solid/>
                                                <span className={`text-[9px] uppercase tracking-wider ${source === "tenant" ? "text-amber-600" : "text-muted-foreground"}`}>{source}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-2 text-right">
                                            <div className="inline-flex gap-1.5">
                                                <button disabled={busyKey === f.key || (isOverridden && tenantOverride === true)}
                                                    onClick={() => toggleFlag(f.key, true)}
                                                    data-testid={`flag-on-${f.key}`}
                                                    className={`px-2.5 py-1 text-[10px] uppercase tracking-wider rounded-md font-semibold ${
                                                        isOverridden && tenantOverride === true
                                                            ? "bg-emerald-500 text-white"
                                                            : "border border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                                                    } disabled:opacity-40`}>ON</button>
                                                <button disabled={busyKey === f.key || (isOverridden && tenantOverride === false)}
                                                    onClick={() => toggleFlag(f.key, false)}
                                                    data-testid={`flag-off-${f.key}`}
                                                    className={`px-2.5 py-1 text-[10px] uppercase tracking-wider rounded-md font-semibold ${
                                                        isOverridden && tenantOverride === false
                                                            ? "bg-rose-500 text-white"
                                                            : "border border-rose-500 text-rose-600 hover:bg-rose-50"
                                                    } disabled:opacity-40`}>OFF</button>
                                                <button disabled={busyKey === f.key || !isOverridden}
                                                    onClick={() => toggleFlag(f.key, null)}
                                                    data-testid={`flag-inherit-${f.key}`}
                                                    title={isOverridden ? "Remove override (inherit from plan/default)" : "No override set"}
                                                    className="px-2.5 py-1 text-[10px] uppercase tracking-wider rounded-md font-semibold border border-border text-muted-foreground hover:text-foreground disabled:opacity-40 inline-flex items-center gap-1">
                                                    <RotateCcw className="w-3 h-3"/>Inherit
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ))}
        </div>
    );
}

/* ── Add-ons ──────────────────────────────────────────────────────────── */
function AddonsTab({ data }) {
    const a = data.addons;
    const ctrl = useTableControls(a.active || [], {
        storageKey: "tenant-detail-addons",
        initialSort: { key: "addon_name", dir: "asc" },
        accessors: {
            addon: r => r.addon_name,
            quantity: r => Number(r.quantity || 0),
            monthly_total_inr: r => Number(r.monthly_total_inr || 0),
            next_charge_at: r => r.next_charge_at || "",
            status: r => r.status || "",
        },
    });
    return (
        <div className="rounded-xl border border-border bg-card overflow-x-auto" data-testid="tenant-addons-tab">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Active add-ons</div>
                    <div className="text-sm text-foreground">{a.active_count} active · {a.suspended_count} suspended · <span className="font-mono">₹{a.monthly_total_inr.toLocaleString("en-IN")}/mo</span></div>
                </div>
            </div>
            {a.active.length === 0 ? (
                <div className="p-6 text-sm text-muted-foreground text-center">No active add-ons.</div>
            ) : (
                <>
                <FilterChip ctrl={ctrl} className="m-3"/>
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                            <SortableTh field="addon" ctrl={ctrl} className="text-left px-4 py-2">Add-on</SortableTh>
                            <SortableTh field="quantity" ctrl={ctrl} className="text-right px-2 py-2" align="right">Qty</SortableTh>
                            <SortableTh field="monthly_total_inr" ctrl={ctrl} className="text-right px-2 py-2" align="right">Monthly</SortableTh>
                            <SortableTh field="next_charge_at" ctrl={ctrl} className="text-right px-2 py-2" align="right">Next charge</SortableTh>
                            <SortableTh field="status" ctrl={ctrl} className="text-right px-4 py-2" align="right">Status</SortableTh>
                        </tr>
                    </thead>
                    <tbody>
                        {ctrl.rows.map(ax => (
                            <tr key={ax.id} className="border-t border-border/50" data-testid={`addon-row-${ax.addon_code}`}>
                                <td className="px-4 py-2">
                                    <div className="text-foreground font-medium">{ax.addon_name}</div>
                                    <div className="text-[10px] text-muted-foreground font-mono">{ax.addon_code}</div>
                                </td>
                                <td className="text-right">{ax.quantity}</td>
                                <td className="text-right font-mono">₹{(ax.monthly_total_inr || 0).toLocaleString("en-IN")}</td>
                                <td className="text-right text-[11px] text-muted-foreground">{ax.next_charge_at ? fmtDate(ax.next_charge_at) : "—"}</td>
                                <td className="text-right">
                                    <span className={`text-[10px] uppercase tracking-wider font-semibold ${ax.renewal_status === "suspended" ? "text-rose-600" : "text-emerald-600"}`}>
                                        {ax.renewal_status || "active"}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </>
            )}
        </div>
    );
}

/* ── Wallet ───────────────────────────────────────────────────────────── */
function WalletTab({ data, tenantId }) {
    return (
        <div className="rounded-xl border border-border bg-card p-5" data-testid="tenant-wallet-tab">
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Wallet balance</div>
            <div className="mt-2 text-3xl font-mono font-semibold text-foreground" data-testid="wallet-balance">
                ₹{data.wallet.balance_inr.toLocaleString("en-IN")}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">{data.wallet.txn_count} historical transactions · {data.wallet.currency}</div>
            <Link to={`/admin/tenants?credit=${tenantId}`} data-testid="wallet-credit-link"
                className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 border border-primary text-primary hover:bg-primary/5 rounded-lg text-xs font-medium">
                <Wallet className="w-3.5 h-3.5"/> Credit wallet (uses existing flow)
            </Link>
        </div>
    );
}

/* ── Settings ─────────────────────────────────────────────────────────── */
function SettingsTab({ data }) {
    const s = data.tenant_settings;
    return (
        <div className="grid md:grid-cols-2 gap-4" data-testid="tenant-settings-tab">
            <Card title="Razorpay" testId="set-razorpay">
                <Row k="Configured" v={s.razorpay_configured ? "✓ Yes" : "— No (uses platform default)"}/>
                <Row k="Key ID" v={s.razorpay_key_id || "—"} mono/>
            </Card>
            <Card title="WhatsApp Business" testId="set-whatsapp">
                <Row k="Configured" v={s.whatsapp_configured ? "✓ Yes" : "— No"}/>
            </Card>
            <Card title="eCourts" testId="set-ecourts">
                <Row k="API key set" v={s.ecourts_configured ? "✓ Yes" : "—"}/>
            </Card>
            <Card title="Trial" testId="set-trial">
                <Row k="Trial ends" v={s.trial_ends_at ? fmtDate(s.trial_ends_at) : "—"}/>
            </Card>
        </div>
    );
}

/* ── Tiny primitives ──────────────────────────────────────────────────── */
function FirmOwnerCard({ owner, addr }) {
    if (!owner) return null;

    const fields = [
        { label: "Full Name",          value: owner.name || "—" },
        { label: "Email",              value: owner.email || "—",  mono: true },
        { label: "Mobile",             value: owner.phone || "—",  mono: true },
        { label: "Bar Council No.",    value: owner.bar_council_number || "—", mono: true },
        { label: "Enrollment Date",    value: owner.enrollment_date || "—" },
        { label: "Account Status",     value: owner.is_active ? "Active" : "Suspended" },
        { label: "Registered On",      value: owner.created_at ? fmtDate(owner.created_at) : "—" },
        { label: "Last Login",         value: owner.last_login_at ? fmtDate(owner.last_login_at) : "—" },
    ];

    const addrFields = [
        { label: "Address",   value: addr?.line || "—" },
        { label: "City",      value: addr?.city || "—" },
        { label: "District",  value: addr?.district || "—" },
        { label: "State",     value: addr?.state || "—" },
        { label: "Pincode",   value: addr?.pincode || "—", mono: true },
        { label: "Country",   value: addr?.country || "India" },
    ];

    return (
        <div className="rounded-xl border border-border bg-card p-4" data-testid="card-firm-owner">
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-4">Firm Owner — Registration Details</div>
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-0">
                <div className="space-y-2">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground/60 mb-2">Identity</div>
                    {fields.map(f => <Row key={f.label} k={f.label} v={f.value} mono={f.mono}/>)}
                </div>
                <div className="space-y-2 mt-4 md:mt-0">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground/60 mb-2">Address</div>
                    {addrFields.map(f => <Row key={f.label} k={f.label} v={f.value} mono={f.mono}/>)}
                </div>
            </div>
        </div>
    );
}

function Card({ title, children, testId }) {
    return (
        <div className="rounded-xl border border-border bg-card p-4" data-testid={testId}>
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-3">{title}</div>
            {children}
        </div>
    );
}

function Row({ k, v, mono }) {
    return (
        <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="text-muted-foreground text-xs">{k}</span>
            <span className={`text-foreground text-right ${mono ? "font-mono" : ""}`}>{v}</span>
        </div>
    );
}

function FlagPill({ v, solid }) {
    const on = v === true;
    const off = v === false;
    if (solid) {
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] uppercase tracking-wider font-semibold ${
                on ? "bg-emerald-500/10 text-emerald-700" : "bg-rose-500/10 text-rose-700"
            }`}>
                {on ? <Check className="w-3 h-3"/> : <X className="w-3 h-3"/>}
                {on ? "ON" : "OFF"}
            </span>
        );
    }
    return (
        <span className={`text-[10px] uppercase tracking-wider font-semibold ${on ? "text-emerald-600" : (off ? "text-rose-600" : "text-muted-foreground")}`}>
            {on ? "on" : off ? "off" : "—"}
        </span>
    );
}

function KpiCard({ label, value, sub, testId }) {
    return (
        <div className="rounded-xl border border-border bg-card p-4" data-testid={testId}>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
            <div className="mt-1.5 text-xl font-mono font-semibold text-foreground">{value}</div>
            {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
        </div>
    );
}


/* ── Team & access (v2.15) ────────────────────────────────────────────── */
const ROLE_LABEL = {
    firm_owner: "Firm owner", firm_admin: "Firm admin", advocate: "Advocate",
    clerk: "Clerk", accountant: "Accountant", client_portal: "Client",
};

function TeamTab({ data, tenantId, reload }) {
    const users = data.users || [];
    const [editing, setEditing] = useState(null);
    const [resetting, setResetting] = useState(null);
    const [tempPw, setTempPw] = useState(null);

    return (
        <div className="space-y-5" data-testid="tenant-team-tab">
            <p className="text-sm text-muted-foreground max-w-2xl">
                All accounts belonging to this tenant. Click <em className="not-italic font-medium">Edit</em> to
                update a user's email, phone or display name. Click <em className="not-italic font-medium">Reset password</em>
                {" "}to generate a new temporary password — the user's active sessions will be revoked and they'll be forced to change it on next login.
            </p>

            <div className="rounded-xl border border-border bg-card overflow-x-auto" data-testid="tenant-team-list">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground bg-muted/30">
                            <th className="text-left px-4 py-2">User</th>
                            <th className="text-left px-4 py-2">Role</th>
                            <th className="text-left px-4 py-2">Email</th>
                            <th className="text-left px-4 py-2">Phone</th>
                            <th className="text-center px-2 py-2 w-24">Active</th>
                            <th className="text-right px-4 py-2 w-72">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.length === 0 && (
                            <tr><td colSpan={6} className="p-6 text-center text-muted-foreground text-sm">No users found for this tenant.</td></tr>
                        )}
                        {users.map(u => (
                            <tr key={u.id} className="border-t border-border/50" data-testid={`team-row-${u.id}`}>
                                <td className="px-4 py-2.5">
                                    <div className="text-foreground font-medium">{u.name || <span className="italic text-muted-foreground">—</span>}</div>
                                    <div className="text-[10px] text-muted-foreground font-mono">{u.id}</div>
                                    {u.must_change_password && (
                                        <div className="text-[10px] text-amber-600 mt-0.5 inline-flex items-center gap-1">
                                            <Key className="w-3 h-3"/> Password change pending
                                        </div>
                                    )}
                                </td>
                                <td className="px-4 py-2.5">
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] uppercase tracking-wider font-semibold bg-muted/40 text-foreground">
                                        {ROLE_LABEL[u.role] || u.role || "—"}
                                    </span>
                                </td>
                                <td className="px-4 py-2.5 font-mono text-xs text-foreground">{u.email || "—"}</td>
                                <td className="px-4 py-2.5 font-mono text-xs text-foreground">{u.phone || "—"}</td>
                                <td className="px-2 py-2.5 text-center">
                                    {u.is_active
                                        ? <span className="inline-flex items-center gap-1 text-emerald-600 text-[10px] font-semibold uppercase"><ShieldCheck className="w-3 h-3"/>Active</span>
                                        : <span className="inline-flex items-center gap-1 text-rose-600 text-[10px] font-semibold uppercase"><ShieldOff className="w-3 h-3"/>Suspended</span>}
                                </td>
                                <td className="px-4 py-2 text-right">
                                    <div className="inline-flex gap-1.5">
                                        <button onClick={() => setEditing(u)} data-testid={`team-edit-${u.id}`}
                                            className="px-2.5 py-1 text-[10px] uppercase tracking-wider rounded-md font-semibold border border-border text-foreground hover:border-primary inline-flex items-center gap-1">
                                            <Mail className="w-3 h-3"/> Edit
                                        </button>
                                        <button onClick={() => setResetting(u)} data-testid={`team-reset-${u.id}`}
                                            className="px-2.5 py-1 text-[10px] uppercase tracking-wider rounded-md font-semibold border border-amber-500 text-amber-600 hover:bg-amber-50 inline-flex items-center gap-1">
                                            <Key className="w-3 h-3"/> Reset password
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editing && (
                <EditContactModal user={editing} tenantId={tenantId}
                    onClose={() => setEditing(null)}
                    onSaved={async () => { setEditing(null); await reload(); }}/>
            )}
            {resetting && (
                <ResetPasswordModal user={resetting} tenantId={tenantId}
                    onClose={() => setResetting(null)}
                    onDone={(res) => { setTempPw(res); setResetting(null); reload(); }}/>
            )}
            {tempPw && <TempPasswordDialog data={tempPw} onClose={() => setTempPw(null)}/>}
        </div>
    );
}

function EditContactModal({ user, tenantId, onClose, onSaved }) {
    const [form, setForm] = useState({
        name: user.name || "", email: user.email || "", phone: user.phone || "",
        is_active: !!user.is_active, reason: "",
    });
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!form.reason || form.reason.trim().length < 3) {
            toast.error("Reason required (3+ chars) — this is audit-logged"); return;
        }
        setSaving(true);
        try {
            const r = await api.patch(`/admin/tenants/${tenantId}/users/${user.id}/contact`, {
                name: form.name.trim(),
                email: form.email.trim(),
                phone: form.phone.trim(),
                is_active: form.is_active,
                reason: form.reason.trim(),
            });
            if (r.data.changed) toast.success(`Updated ${r.data.updated_fields.join(", ")}`);
            else toast.info("No changes to save");
            await onSaved();
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="team-edit-modal">
            <div className="bg-card border border-border rounded-xl w-full max-w-lg p-5 space-y-4">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Edit user · {ROLE_LABEL[user.role] || user.role}</div>
                    <h3 className="text-lg font-semibold text-foreground mt-0.5">{user.name || user.email}</h3>
                </div>
                <div className="space-y-3">
                    <TeamField label="Full name" value={form.name} onChange={v => setForm({...form, name: v})} testId="team-edit-name"/>
                    <TeamField label="Email · login identifier" value={form.email} onChange={v => setForm({...form, email: v})} testId="team-edit-email" type="email"/>
                    <TeamField label="Phone" value={form.phone} onChange={v => setForm({...form, phone: v})} testId="team-edit-phone"/>
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={form.is_active}
                            onChange={e => setForm({...form, is_active: e.target.checked})}
                            data-testid="team-edit-active"
                            className="accent-emerald-500"/>
                        <span className="text-foreground">Account active</span>
                        {!form.is_active && <span className="text-[10px] text-rose-500">(user will not be able to log in)</span>}
                    </label>
                    <TeamField label="Reason for change (audit-logged, 3+ chars)" value={form.reason}
                        onChange={v => setForm({...form, reason: v})} testId="team-edit-reason"/>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <button onClick={onClose} disabled={saving} data-testid="team-edit-cancel"
                        className="px-4 py-2 text-sm border border-border rounded-lg hover:border-foreground text-muted-foreground">Cancel</button>
                    <button onClick={save} disabled={saving} data-testid="team-edit-save"
                        className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90 disabled:opacity-40 inline-flex items-center gap-1.5">
                        <Save className="w-3.5 h-3.5"/> {saving ? "Saving…" : "Save changes"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function ResetPasswordModal({ user, tenantId, onClose, onDone }) {
    const [customPw, setCustomPw] = useState("");
    const [mustChange, setMustChange] = useState(true);
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (reason.trim().length < 3) { toast.error("Reason required (3+ chars)"); return; }
        if (customPw && customPw.length < 10) { toast.error("Custom password must be 10+ chars, or leave blank to auto-generate"); return; }
        setBusy(true);
        try {
            const r = await api.post(`/admin/tenants/${tenantId}/users/${user.id}/reset-password`, {
                new_password: customPw || null,
                require_change_on_next_login: mustChange,
                reason: reason.trim(),
            });
            onDone(r.data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="team-reset-modal">
            <div className="bg-card border border-amber-500/60 rounded-xl w-full max-w-lg p-5 space-y-4">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-amber-500 inline-flex items-center gap-1.5"><Key className="w-3 h-3"/> Reset password</div>
                    <h3 className="text-lg font-semibold text-foreground mt-0.5">{user.email}</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                        Generates a new temporary password. All active sessions for this user will be revoked immediately.
                    </p>
                </div>
                <div className="space-y-3">
                    <TeamField label="Custom password (leave blank to auto-generate 14-char secure random)"
                        value={customPw} onChange={setCustomPw} testId="team-reset-custom-pw" type="text" placeholder="—"/>
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={mustChange} onChange={e => setMustChange(e.target.checked)}
                            data-testid="team-reset-must-change" className="accent-emerald-500"/>
                        <span className="text-foreground">Force user to change password on next login</span>
                    </label>
                    <TeamField label="Reason (audit-logged, 3+ chars)" value={reason} onChange={setReason} testId="team-reset-reason"/>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <button onClick={onClose} disabled={busy} data-testid="team-reset-cancel"
                        className="px-4 py-2 text-sm border border-border rounded-lg hover:border-foreground text-muted-foreground">Cancel</button>
                    <button onClick={submit} disabled={busy} data-testid="team-reset-submit"
                        className="px-4 py-2 text-sm bg-amber-500 text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-40 inline-flex items-center gap-1.5">
                        <Key className="w-3.5 h-3.5"/> {busy ? "Resetting…" : "Generate new password"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function TempPasswordDialog({ data, onClose }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(data.temp_password);
            setCopied(true); toast.success("Password copied to clipboard");
            setTimeout(() => setCopied(false), 3000);
        } catch { toast.error("Copy failed — please select manually"); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="team-temp-pw-dialog">
            <div className="bg-card border-2 border-emerald-500 rounded-xl w-full max-w-lg p-5 space-y-4 shadow-2xl">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-500 inline-flex items-center gap-1.5"><Check className="w-3 h-3"/> Password reset · shown only once</div>
                    <h3 className="text-lg font-semibold text-foreground mt-0.5">{data.user_email}</h3>
                </div>
                <div className="p-3 rounded-lg bg-muted/50 border border-dashed border-border">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Temporary password</div>
                    <div className="mt-1 flex items-center gap-2">
                        <code data-testid="team-temp-pw-value" className="flex-1 font-mono text-lg text-foreground select-all">{data.temp_password}</code>
                        <button onClick={copy} data-testid="team-temp-pw-copy"
                            className="px-3 py-2 border border-emerald-500 text-emerald-600 rounded-md hover:bg-emerald-50 inline-flex items-center gap-1 text-xs font-semibold">
                            <Copy className="w-3.5 h-3.5"/> {copied ? "Copied" : "Copy"}
                        </button>
                    </div>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{data.note}</p>
                <div className="flex justify-end">
                    <button onClick={onClose} data-testid="team-temp-pw-close"
                        className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg font-medium hover:opacity-90">
                        I've saved it — close
                    </button>
                </div>
            </div>
        </div>
    );
}

function TeamField({ label, value, onChange, testId, type = "text", placeholder }) {
    return (
        <div>
            <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground block mb-1">{label}</label>
            <input type={type} value={value} onChange={e => onChange(e.target.value)}
                data-testid={testId} placeholder={placeholder}
                className="w-full bg-background border border-border px-3 py-2 text-sm text-foreground focus:border-primary outline-none rounded-md"/>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   NEW TABS — v2.25
   ═══════════════════════════════════════════════════════════════════════════ */

/* ── Shared helpers ─────────────────────────────────────────────────── */
function TabLoader() {
    return <div className="flex items-center justify-center py-20 text-muted-foreground text-sm">Loading…</div>;
}
function TabError({ msg }) {
    return (
        <div className="flex items-center gap-2 text-destructive text-sm py-10 justify-center">
            <AlertTriangle className="w-4 h-4"/> {msg}
        </div>
    );
}
function fmtINR(v) {
    return `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function StatusPill({ v }) {
    const clr = v === "success" || v === "delivered" || v === "sent"
        ? "bg-emerald-500/10 text-emerald-700"
        : v === "failed" || v === "bounced"
        ? "bg-rose-500/10 text-rose-700"
        : "bg-muted text-muted-foreground";
    return <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase font-semibold ${clr}`}>{v || "—"}</span>;
}

/* ── Dashboard View Tab ─────────────────────────────────────────────── */
function DashboardViewTab({ tenantId }) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    useEffect(() => {
        api.get(`/admin/tenants/${tenantId}/dashboard`)
            .then(r => setData(r.data))
            .catch(e => setErr(apiError(e)));
    }, [tenantId]);
    if (err) return <TabError msg={err}/>;
    if (!data) return <TabLoader/>;
    const metrics = [
        { label: "Hearings today",   value: data.hearings_today,    icon: Gavel },
        { label: "Hearings tomorrow",value: data.hearings_tomorrow, icon: Gavel },
        { label: "Upcoming (7d)",    value: data.upcoming_hearings, icon: Calendar },
        { label: "Overdue tasks",    value: data.overdue_items,     icon: AlertTriangle },
        { label: "Active cases",     value: data.active_cases,      icon: TrendingUp },
        { label: "Total cases",      value: data.total_cases,       icon: Building2 },
        { label: "Wallet balance",   value: fmtINR(data.wallet_balance), icon: Wallet },
        { label: "eCourts spend 30d",value: fmtINR(data.ecourts_spend_30d), icon: CreditCard },
    ];
    return (
        <div className="space-y-5" data-testid="tenant-dashboard-tab">
            <div className="text-xs text-muted-foreground">Read-only view of tenant's current dashboard state — {data.today}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {metrics.map(m => (
                    <div key={m.label} className="rounded-xl border border-border bg-card p-4">
                        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                            <m.icon className="w-3 h-3"/>{m.label}
                        </div>
                        <div className="text-xl font-mono font-semibold text-foreground">{m.value}</div>
                    </div>
                ))}
            </div>
            {Object.keys(data.cases_by_status || {}).length > 0 && (
                <div className="rounded-xl border border-border bg-card p-4">
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-3">Cases by status</div>
                    <div className="flex flex-wrap gap-2">
                        {Object.entries(data.cases_by_status).map(([status, count]) => (
                            <div key={status} className="flex items-center gap-1.5 bg-muted px-3 py-1.5 rounded-lg text-sm">
                                <span className="font-mono font-semibold text-foreground">{count}</span>
                                <span className="text-muted-foreground capitalize">{status || "unknown"}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

/* ── Passbook Tab ────────────────────────────────────────────────────── */
function PassbookTab({ tenantId, data }) {
    const [pbData, setPbData] = useState(null);
    const [err, setErr] = useState(null);
    const [view, setView] = useState("ecourts"); // ecourts | wallet
    useEffect(() => {
        api.get(`/admin/tenants/${tenantId}/passbook?limit=100`)
            .then(r => setPbData(r.data))
            .catch(e => setErr(apiError(e)));
    }, [tenantId]);
    if (err) return <TabError msg={err}/>;
    if (!pbData) return <TabLoader/>;
    return (
        <div className="space-y-5" data-testid="tenant-passbook-tab">
            {/* Balance summary */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                <div className="rounded-xl border border-border bg-card p-4">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Wallet balance</div>
                    <div className="text-2xl font-mono font-semibold text-foreground mt-1">{fmtINR(data.wallet.balance_inr)}</div>
                </div>
                <div className="rounded-xl border border-border bg-card p-4">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total eCourts charges</div>
                    <div className="text-2xl font-mono font-semibold text-foreground mt-1">{pbData.total} txns</div>
                </div>
                <div className="rounded-xl border border-border bg-card p-4">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Wallet transactions</div>
                    <div className="text-2xl font-mono font-semibold text-foreground mt-1">{pbData.wallet_txns.length} txns</div>
                </div>
            </div>
            {/* Toggle */}
            <div className="flex gap-2">
                {["ecourts", "wallet"].map(v => (
                    <button key={v} onClick={() => setView(v)}
                        className={`px-4 py-1.5 rounded-lg text-sm font-medium border transition-colors ${view === v ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground hover:text-foreground"}`}>
                        {v === "ecourts" ? "eCourts charges" : "Wallet top-ups"}
                    </button>
                ))}
            </div>
            {view === "ecourts" && (
                <div className="rounded-xl border border-border bg-card overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="border-b border-border bg-muted/30">
                            <tr>
                                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">CNR</th>
                                <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Event</th>
                                <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Strategy</th>
                                <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Cost (₹)</th>
                                <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Balance after</th>
                                <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pbData.ecourts_rows.length === 0 && (
                                <tr><td colSpan={6} className="text-center py-8 text-muted-foreground text-sm">No eCourts charges yet</td></tr>
                            )}
                            {pbData.ecourts_rows.map(r => (
                                <tr key={r.id} className="border-b border-border hover:bg-muted/20">
                                    <td className="px-4 py-2.5 font-mono text-xs text-foreground">{r.case_cnr}</td>
                                    <td className="px-3 py-2.5 text-xs text-muted-foreground">{r.event}</td>
                                    <td className="px-3 py-2.5 text-xs text-muted-foreground">{r.strategy}</td>
                                    <td className="px-4 py-2.5 text-right font-mono text-sm text-rose-600">{fmtINR(r.amount_inr)}</td>
                                    <td className="px-4 py-2.5 text-right font-mono text-xs text-foreground">{fmtINR(r.balance_after)}</td>
                                    <td className="px-4 py-2.5 text-right text-xs text-muted-foreground">{r.created_at?.slice(0, 16).replace("T", " ")}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {view === "wallet" && (
                <div className="rounded-xl border border-border bg-card overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="border-b border-border bg-muted/30">
                            <tr>
                                <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Type</th>
                                <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Description</th>
                                <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Amount (₹)</th>
                                <th className="text-right px-4 py-2.5 font-medium text-muted-foreground">Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pbData.wallet_txns.length === 0 && (
                                <tr><td colSpan={4} className="text-center py-8 text-muted-foreground text-sm">No wallet transactions</td></tr>
                            )}
                            {pbData.wallet_txns.map(r => (
                                <tr key={r.id} className="border-b border-border hover:bg-muted/20">
                                    <td className="px-4 py-2.5 text-xs font-medium capitalize text-foreground">{r.type}</td>
                                    <td className="px-3 py-2.5 text-xs text-muted-foreground">{r.description || "—"}</td>
                                    <td className={`px-4 py-2.5 text-right font-mono text-sm ${r.amount_inr >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{fmtINR(r.amount_inr)}</td>
                                    <td className="px-4 py-2.5 text-right text-xs text-muted-foreground">{r.created_at?.slice(0, 16).replace("T", " ")}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

/* ── Audit Log Tab ───────────────────────────────────────────────────── */
function AuditLogTab({ tenantId }) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    useEffect(() => {
        api.get(`/admin/tenants/${tenantId}/audit-log?limit=50`)
            .then(r => setData(r.data))
            .catch(e => setErr(apiError(e)));
    }, [tenantId]);
    if (err) return <TabError msg={err}/>;
    if (!data) return <TabLoader/>;
    return (
        <div className="space-y-4" data-testid="tenant-audit-tab">
            <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">{data.total} total events · showing latest 50</div>
            </div>
            <div className="rounded-xl border border-border bg-card overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="border-b border-border bg-muted/30">
                        <tr>
                            <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Timestamp</th>
                            <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Event</th>
                            <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Actor</th>
                            <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Scope</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.rows.length === 0 && (
                            <tr><td colSpan={4} className="text-center py-8 text-muted-foreground text-sm">No audit events found</td></tr>
                        )}
                        {data.rows.map(r => (
                            <tr key={r.id} className="border-b border-border hover:bg-muted/20">
                                <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{(r.timestamp || "").slice(0, 19).replace("T", " ")}</td>
                                <td className="px-3 py-2.5 text-xs font-mono text-foreground">{r.event}</td>
                                <td className="px-3 py-2.5 text-xs text-muted-foreground">{r.user_email}</td>
                                <td className="px-3 py-2.5">
                                    <span className="inline-block px-2 py-0.5 rounded bg-muted text-[10px] text-muted-foreground uppercase tracking-wider">{r.scope}</span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/* ── Sessions Tab ────────────────────────────────────────────────────── */
function SessionsTab({ tenantId }) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [loggingOut, setLoggingOut] = useState(false);
    const load = useCallback(async () => {
        try {
            const r = await api.get(`/admin/tenants/${tenantId}/sessions`);
            setData(r.data);
        } catch (e) { setErr(apiError(e)); }
    }, [tenantId]);
    useEffect(() => { load(); }, [load]);

    const forceLogout = async () => {
        if (!window.confirm("Force-logout ALL users in this tenant? They will be signed out immediately.")) return;
        setLoggingOut(true);
        try {
            const r = await api.post(`/admin/tenants/${tenantId}/sessions/force-logout`);
            toast.success(`Revoked sessions for ${r.data.revoked_users} users`);
            load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoggingOut(false); }
    };

    if (err) return <TabError msg={err}/>;
    if (!data) return <TabLoader/>;
    return (
        <div className="space-y-4" data-testid="tenant-sessions-tab">
            <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">{data.count} users with session history</div>
                <button onClick={forceLogout} disabled={loggingOut} data-testid="tenant-force-logout-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-destructive text-destructive hover:bg-destructive/5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50">
                    <LogOut className="w-3.5 h-3.5"/>
                    {loggingOut ? "Revoking…" : "Force logout all"}
                </button>
            </div>
            <div className="rounded-xl border border-border bg-card overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="border-b border-border bg-muted/30">
                        <tr>
                            <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">User</th>
                            <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Device</th>
                            <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">
                                <span className="flex items-center gap-1"><MapPin className="w-3 h-3"/> Location</span>
                            </th>
                            <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">IP</th>
                            <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Last seen</th>
                            <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.rows.length === 0 && (
                            <tr><td colSpan={6} className="text-center py-8 text-muted-foreground text-sm">No session history</td></tr>
                        )}
                        {data.rows.map(r => (
                            <tr key={r.user_id} className="border-b border-border hover:bg-muted/20">
                                <td className="px-4 py-2.5">
                                    <div className="text-sm font-medium text-foreground">{r.name}</div>
                                    <div className="text-xs text-muted-foreground">{r.email} · <span className="capitalize">{r.role}</span></div>
                                </td>
                                <td className="px-3 py-2.5">
                                    <div className="text-xs text-foreground flex items-center gap-1">
                                        <Cpu className="w-3 h-3 text-muted-foreground"/>{r.device?.browser} / {r.device?.os}
                                    </div>
                                </td>
                                <td className="px-3 py-2.5">
                                    {r.location?.city ? (
                                        <div className="text-xs text-foreground flex items-center gap-1">
                                            <Globe className="w-3 h-3 text-muted-foreground"/>
                                            {[r.location.city, r.location.region, r.location.country].filter(Boolean).join(", ")}
                                        </div>
                                    ) : (
                                        <span className="text-xs text-muted-foreground">—</span>
                                    )}
                                </td>
                                <td className="px-3 py-2.5 font-mono text-xs text-muted-foreground">{r.last_login_ip || "—"}</td>
                                <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{(r.last_login_at || "").slice(0, 16).replace("T", " ")}</td>
                                <td className="px-3 py-2.5">
                                    <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${r.active ? "bg-emerald-500/10 text-emerald-700" : "bg-rose-500/10 text-rose-700"}`}>
                                        {r.active ? "active" : "revoked"}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/* ── Usage Analytics Tab ─────────────────────────────────────────────── */
function UsageTab({ tenantId }) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    useEffect(() => {
        api.get(`/admin/tenants/${tenantId}/usage`)
            .then(r => setData(r.data))
            .catch(e => setErr(apiError(e)));
    }, [tenantId]);
    if (err) return <TabError msg={err}/>;
    if (!data) return <TabLoader/>;
    const kpis = [
        { label: "Total cases",       value: data.total_cases },
        { label: "Total users",       value: data.total_users },
        { label: "Active users (30d)",value: data.active_users_30d },
        { label: "Documents",         value: data.docs_count },
        { label: "Tasks done (30d)",  value: data.tasks_done_30d },
        { label: "eCourts syncs (30d)",value: data.ecourts_calls_30d },
        { label: "eCourts spend (30d)",value: fmtINR(data.ecourts_spend_30d) },
    ];
    const maxCases = Math.max(...data.monthly_trend.map(m => m.cases), 1);
    return (
        <div className="space-y-5" data-testid="tenant-usage-tab">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {kpis.map(k => (
                    <div key={k.label} className="rounded-xl border border-border bg-card p-4">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k.label}</div>
                        <div className="text-xl font-mono font-semibold text-foreground mt-1">{k.value}</div>
                    </div>
                ))}
            </div>
            {/* Monthly trend bar chart */}
            <div className="rounded-xl border border-border bg-card p-5">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-4">Cases created — last 6 months</div>
                <div className="flex items-end gap-2 h-28">
                    {data.monthly_trend.map(m => (
                        <div key={m.label} className="flex-1 flex flex-col items-center gap-1">
                            <div className="text-[10px] text-muted-foreground font-mono">{m.cases}</div>
                            <div className="w-full bg-primary/80 rounded-t transition-all"
                                style={{ height: `${Math.max(4, (m.cases / maxCases) * 80)}px` }}/>
                            <div className="text-[9px] text-muted-foreground text-center leading-tight">{m.label.split(" ")[0]}</div>
                        </div>
                    ))}
                </div>
            </div>
            {/* eCourts syncs trend */}
            <div className="rounded-xl border border-border bg-card p-5">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-4">eCourts syncs — last 6 months</div>
                <div className="flex items-end gap-2 h-20">
                    {(() => {
                        const maxS = Math.max(...data.monthly_trend.map(m => m.ecourts_syncs), 1);
                        return data.monthly_trend.map(m => (
                            <div key={m.label} className="flex-1 flex flex-col items-center gap-1">
                                <div className="text-[10px] text-muted-foreground font-mono">{m.ecourts_syncs}</div>
                                <div className="w-full bg-indigo-500/70 rounded-t transition-all"
                                    style={{ height: `${Math.max(4, (m.ecourts_syncs / maxS) * 60)}px` }}/>
                                <div className="text-[9px] text-muted-foreground text-center">{m.label.split(" ")[0]}</div>
                            </div>
                        ));
                    })()}
                </div>
            </div>
        </div>
    );
}

/* ── Communications Tab ──────────────────────────────────────────────── */
function CommsTab({ tenantId }) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    useEffect(() => {
        api.get(`/admin/tenants/${tenantId}/communications?limit=50`)
            .then(r => setData(r.data))
            .catch(e => setErr(apiError(e)));
    }, [tenantId]);
    if (err) return <TabError msg={err}/>;
    if (!data) return <TabLoader/>;
    return (
        <div className="space-y-4" data-testid="tenant-comms-tab">
            <div className="text-sm text-muted-foreground">{data.total} total communications · showing latest 50</div>
            <div className="rounded-xl border border-border bg-card overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="border-b border-border bg-muted/30">
                        <tr>
                            <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Sent at</th>
                            <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Channel</th>
                            <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Event</th>
                            <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Recipient</th>
                            <th className="text-left px-3 py-2.5 font-medium text-muted-foreground">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.rows.length === 0 && (
                            <tr><td colSpan={5} className="text-center py-8 text-muted-foreground text-sm">No communication records found</td></tr>
                        )}
                        {data.rows.map(r => (
                            <tr key={r.id} className="border-b border-border hover:bg-muted/20">
                                <td className="px-4 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{(r.sent_at || "").slice(0, 16).replace("T", " ")}</td>
                                <td className="px-3 py-2.5">
                                    <span className="inline-block px-2 py-0.5 rounded bg-muted text-[10px] uppercase font-medium capitalize">{r.channel}</span>
                                </td>
                                <td className="px-3 py-2.5 text-xs text-foreground">{r.event}</td>
                                <td className="px-3 py-2.5 text-xs text-muted-foreground">{r.recipient}</td>
                                <td className="px-3 py-2.5"><StatusPill v={r.status}/></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
